import React, { useState, useEffect } from 'react';
import { getCookieSettings, saveCookieSettings, CookieSettings } from '../utils/cookieManager';
import { Shield, Info } from 'lucide-react';

const PrivacySettings: React.FC = () => {
  const [settings, setSettings] = useState<CookieSettings>(getCookieSettings());
  const [saved, setSaved] = useState(false);

  const handleCookieToggle = (type: keyof CookieSettings) => {
    // Don't allow toggling essential cookies
    if (type === 'essential') return;
    
    setSettings(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    
    // Reset saved status
    setSaved(false);
  };

  const saveSettings = () => {
    saveCookieSettings(settings);
    setSaved(true);
    
    // Reset saved indicator after 3 seconds
    setTimeout(() => {
      setSaved(false);
    }, 3000);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-4">
        <Shield className="h-5 w-5 text-blue-600 mr-2" />
        <h2 className="text-xl font-semibold text-gray-900">Privacy Settings</h2>
      </div>
      
      <p className="text-gray-600 mb-6">
        Manage your cookie preferences and privacy settings. Essential cookies are always enabled as they are necessary for the website to function properly.
      </p>
      
      <div className="space-y-4">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="essential"
              type="checkbox"
              checked={settings.essential}
              disabled
              className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed opacity-60"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="essential" className="font-medium text-gray-900 flex items-center">
              Essential Cookies
              <Shield className="h-4 w-4 ml-1 text-green-600" />
            </label>
            <p className="text-gray-500">
              These cookies are necessary for the website to function properly and cannot be disabled.
            </p>
          </div>
        </div>
        
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="analytics"
              type="checkbox"
              checked={settings.analytics}
              onChange={() => handleCookieToggle('analytics')}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="analytics" className="font-medium text-gray-900">Analytics Cookies</label>
            <p className="text-gray-500">
              These cookies help us understand how visitors interact with our website, helping us improve our services.
            </p>
          </div>
        </div>
        
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="marketing"
              type="checkbox"
              checked={settings.marketing}
              onChange={() => handleCookieToggle('marketing')}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="marketing" className="font-medium text-gray-900">Marketing Cookies</label>
            <p className="text-gray-500">
              These cookies are used to track visitors across websites to display relevant advertisements.
            </p>
          </div>
        </div>
        
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="preferences"
              type="checkbox"
              checked={settings.preferences}
              onChange={() => handleCookieToggle('preferences')}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="preferences" className="font-medium text-gray-900">Preference Cookies</label>
            <p className="text-gray-500">
              These cookies enable personalized features and remember your preferences across our website.
            </p>
          </div>
        </div>
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
        <div className="flex items-center text-sm text-gray-500">
          <Info className="h-4 w-4 mr-1" />
          <span>Your settings will be saved in your browser.</span>
        </div>
        <button
          onClick={saveSettings}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center"
        >
          {saved ? 'Settings Saved!' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default PrivacySettings;